import { UserProfileShortPopoverTrigger } from '@modules/user/UserProfileShortPopoverTrigger';
import { NodeViewWrapper } from '@tiptap/react';
import classNames from 'classnames';
import { useCallback, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';
import {
  CodeHighlight,
  CodeHighlightRender,
} from 'src/components/CodeHighlight';
import useCopyToClipboard from 'src/hooks/useCopyToClipboard';
import { useLocales } from 'src/locales';
import { getEmojiNative } from 'src/modules/emoji/utils/getEmojiNative';
import { MentionDocNodeView } from '../../private/components/MentionDocNodeView';
import { MentionTableRowNodeView } from '../../private/components/MentionTableRowNodeView';
import { getExtensionOption } from '../../private/utils/getExtensionOption';
import {
  BasicNode,
  EmojiNode,
  Mapping,
  MentionNode,
  type MentionTableRowNode,
  RendererProps,
} from './types';

export const nodes: Mapping = {
  bulletList: ({
    editor: _,
    node: { attrs },
    children,
  }: RendererProps<BasicNode>) => <ul {...attrs}>{children}</ul>,
  doc: ({ editor: _, node: { attrs }, children }: RendererProps<BasicNode>) => (
    <div {...attrs}>{children}</div>
  ),
  listItem: ({
    editor: _,
    node: { attrs },
    children,
  }: RendererProps<BasicNode>) => <li {...attrs}>{children}</li>,
  orderedList: ({
    editor: _,
    node: { attrs },
    children,
  }: RendererProps<BasicNode>) => <ol {...attrs}>{children}</ol>,
  hardBreak: () => <br />,
  horizontalRule: () => <hr />,
  image: ({
    editor: _,
    node: { attrs },
    children,
  }: RendererProps<BasicNode>) => <img {...attrs} />,
  blockquote: ({
    editor: _,
    node: { attrs },
    children,
  }: RendererProps<BasicNode>) => (
    <blockquote {...attrs}>{children}</blockquote>
  ),
  heading: ({ editor: _, node, children }: RendererProps<BasicNode>) => {
    const Tag = `h${node.attrs?.level ?? 3}` as any;
    return <Tag>{children}</Tag>;
  },
  paragraph: ({
    editor: _,
    node: { attrs },
    children,
  }: RendererProps<BasicNode>) => <p {...attrs}>{children}</p>,
  'emoji-search': ({
    editor: _,
    node: { attrs },
  }: RendererProps<EmojiNode>) => {
    const native = useMemo(() => getEmojiNative(attrs.id), [attrs.id]);
    return <span {...attrs}>{native || ''}</span>;
  },
  mention: ({ editor, node }: RendererProps<MentionNode>) => {
    const extension = useMemo(
      () =>
        editor.extensionManager.extensions.find(
          (ext) => ext.name === 'mention',
        ),
      [editor.extensionManager.extensions],
    );

    const isCurrentUser =
      getExtensionOption(editor, 'userMention').currentUserId === node.attrs.id;

    // Isso aqui foi o que resolveu o erro do paste link
    return (
      <NodeViewWrapper as="span">
        <UserProfileShortPopoverTrigger userId={node.attrs.id}>
          <span
            className={classNames('user-mention', { '--me': isCurrentUser })}
            contentEditable={false}
          >
            {extension?.options.renderLabel.call(
              { editor },
              { node, options: extension.options },
            )}
          </span>
        </UserProfileShortPopoverTrigger>
      </NodeViewWrapper>
    );
  },
  mentionDoc: ({ editor, node }: RendererProps<MentionNode>) => {
    const doc = useMemo(() => {
      if (!node.attrs.id) return null;
      const options = getExtensionOption(editor, 'mentionDoc');
      return options.getDocumentById(node.attrs.id);
    }, [editor, node.attrs.id]);

    const onClick = useCallback(() => {
      if (!doc) return;
      const options = getExtensionOption(editor, 'mentionDoc');
      options.onClick?.(doc);
    }, [editor, doc]);

    const to = useMemo(() => {
      if (!doc) return undefined;
      const options = getExtensionOption(editor, 'mentionDoc');
      return options.getDocumentPath?.(doc);
    }, [editor, doc]);

    if (!doc) return null;

    return <MentionDocNodeView doc={doc} onClick={onClick} to={to} />;
  },
  mentionTableRow: ({ editor, node }: RendererProps<MentionTableRowNode>) => {
    const { rowId, tableId, boardId } = node.attrs;

    const onClick = useCallback(() => {
      const options = getExtensionOption(editor, 'mentionTableRow');
      options.onClick?.(rowId, boardId, tableId);
    }, [editor, rowId, boardId, tableId]);

    if (!rowId) return null;

    return (
      <MentionTableRowNodeView
        boardId={boardId}
        tableId={tableId}
        rowId={rowId}
        onClick={onClick}
      />
    );
  },
  codeBlock: ({ editor, node }: RendererProps<BasicNode>) => {
    const { copy } = useCopyToClipboard();
    const { enqueueSnackbar } = useSnackbar();
    const { translate } = useLocales();

    const [selectedLang, setSelectedLang] = useState<string>(
      (node?.attrs?.language as string) || '',
    );

    const handleCopyCode = async () => {
      await copy((node?.content?.[0] as any)?.text);
      enqueueSnackbar(translate('shared.editor.code_in_your_clipboard'));
    };

    return (
      <CodeHighlight
        language={selectedLang}
        onLanguageChange={(lang) => {
          setSelectedLang(lang);
        }}
        copyCode={handleCopyCode}
      >
        <CodeHighlightRender
          code={(node?.content?.[0] as any)?.text || ''}
          language={selectedLang}
        />
      </CodeHighlight>
    );
  },
};
